import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  createGroup, 
  getAllGroups, 
  updateGroup, 
  deleteGroup,
  getGroupMembers 
} from '../../services/firestoreService';

const GroupsManagement = () => {
  const { hasRole, currentUser } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    category: 'professional',
    isPrivate: false,
    maxMembers: null
  });

  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (!isAdmin) return;
    loadGroups();
  }, [isAdmin]);

  const loadGroups = async () => {
    try {
      const groupsData = await getAllGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      const groupData = {
        ...newGroup,
        createdAt: new Date(),
        memberCount: 0,
        isActive: true
      };
      
      await createGroup(groupData, currentUser?.uid);
      setNewGroup({
        name: '',
        description: '',
        category: 'professional',
        isPrivate: false,
        maxMembers: null
      });
      setShowCreateForm(false);
      loadGroups();
      alert('Group created successfully!');
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Error creating group. Please try again.');
    }
  };

  const handleUpdateGroup = async (groupId, updates) => {
    try {
      await updateGroup(groupId, updates);
      loadGroups();
      setEditingGroup(null);
      alert('Group updated successfully!');
    } catch (error) {
      console.error('Error updating group:', error);
      alert('Error updating group. Please try again.');
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (window.confirm(`Are you sure you want to delete "${groupName}"? This action cannot be undone.`)) {
      try {
        await deleteGroup(groupId);
        loadGroups();
        alert('Group deleted successfully!');
      } catch (error) {
        console.error('Error deleting group:', error);
        alert('Error deleting group. Please try again.');
      }
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Groups Management</h1>
        <p className="text-gray-600">Create, edit, and manage community groups</p>
      </div>

      {/* Create Group Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-apn-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
        >
          Create New Group
        </button>
      </div>

      {/* Create Group Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Create New Group</h2>
          <form onSubmit={handleCreateGroup}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Group Name *
                </label>
                <input
                  type="text"
                  required
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={newGroup.category}
                  onChange={(e) => setNewGroup({...newGroup, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="professional">Professional</option>
                  <option value="industry">Industry Specific</option>
                  <option value="location">Location Based</option>
                  <option value="interest">Special Interest</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                required
                rows={3}
                value={newGroup.description}
                onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>

            <div className="mt-4 flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newGroup.isPrivate}
                  onChange={(e) => setNewGroup({...newGroup, isPrivate: e.target.checked})}
                  className="mr-2"
                />
                Private Group
              </label>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max Members (optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={newGroup.maxMembers || ''}
                  onChange={(e) => setNewGroup({...newGroup, maxMembers: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                type="submit"
                className="bg-apn-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
              >
                Create Group
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Groups List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">All Groups ({groups.length})</h2>
        </div>
        
        {groups.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No groups found. Create your first group to get started.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groups.map((group) => (
              <div key={group.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                    <p className="text-gray-600 mt-1">{group.description}</p>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>Category: {group.category}</span>
                      <span>Members: {group.memberCount || 0}</span>
                      {group.isPrivate && <span className="text-red-600">Private</span>}
                      {group.maxMembers && <span>Max: {group.maxMembers}</span>}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingGroup(group)}
                      className="text-apn-blue hover:text-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id, group.name)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Group Modal */}
      {editingGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Group</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const updates = {
                name: formData.get('name'),
                description: formData.get('description'),
                category: formData.get('category'),
                isPrivate: formData.get('isPrivate') === 'on',
                maxMembers: formData.get('maxMembers') ? parseInt(formData.get('maxMembers')) : null
              };
              handleUpdateGroup(editingGroup.id, updates);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingGroup.name}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    defaultValue={editingGroup.description}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    defaultValue={editingGroup.category}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  >
                    <option value="professional">Professional</option>
                    <option value="industry">Industry Specific</option>
                    <option value="location">Location Based</option>
                    <option value="interest">Special Interest</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isPrivate"
                      defaultChecked={editingGroup.isPrivate}
                      className="mr-2"
                    />
                    Private Group
                  </label>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Max Members
                    </label>
                    <input
                      type="number"
                      name="maxMembers"
                      min="1"
                      defaultValue={editingGroup.maxMembers || ''}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex space-x-3">
                <button
                  type="submit"
                  className="bg-apn-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
                >
                  Update Group
                </button>
                <button
                  type="button"
                  onClick={() => setEditingGroup(null)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsManagement;
